import { SimCardDetailSimStatus } from "@/services/model";

/** Everything the redirect rules need to decide where the view should send the user. */
export interface DeviceManagementRedirectInputs {
  /** The raw `simId` query param. */
  simId: string;
  /** Status of the displayed SIM. */
  simStatus: SimCardDetailSimStatus | undefined;
  /** Whether the customer still has to complete an ID check. */
  needsIdCheck: boolean | undefined;
  /** Whether an eSIM reset/transfer/physical-to-eSIM change is in progress. */
  isModifyingESim: boolean;
  /** The wildcard part of the management route (e.g. `"subscription/display"`). */
  managementSubPath: string | undefined;
  /** Whether the SIM is currently going through number porting. */
  isNumberPorting: boolean;
}

/**
 * Resolves the guard/redirect logic of the device management view into a single
 * target path, or `null` when the current route may stay as it is. The caller
 * renders a replacing `<Navigate>` for any non-null result.
 *
 * The rules are evaluated in order of precedence:
 * 1. A missing SIM, a blocked (red) SIM, or a pending ID check sends the user
 *    back to the overview.
 * 2. While an eSIM change is in progress the Details tab is the only place
 *    that renders its (inline) waiting/ready state, so any other tab bounces
 *    there.
 * 3. The subscription can only be managed once number porting is through, so a
 *    porting SIM is bounced off the Abo tab onto the Details tab.
 */
export const resolveDeviceManagementRedirect = ({
  simId,
  simStatus,
  needsIdCheck,
  isModifyingESim,
  managementSubPath,
  isNumberPorting,
}: DeviceManagementRedirectInputs): string | null => {
  // Navigate back to overview if we can't find the sim or this is a blocked sim.
  if (!simId || simStatus === SimCardDetailSimStatus.red || needsIdCheck) {
    return "..";
  }

  // The Details tab is the only place that shows an in-progress eSIM change,
  // so keep the user there for its duration.
  if (isModifyingESim && !managementSubPath?.startsWith("details")) {
    return `details?simId=${simId}`;
  }

  // The subscription can only be managed once the number porting is through,
  // so a porting SIM lands on the Details tab instead of the Abo tab.
  if (isNumberPorting && managementSubPath?.startsWith("subscription")) {
    return `details?simId=${simId}`;
  }

  return null;
};
